import { Field, useFormikContext } from "formik";
import { useEffect, useMemo, useState } from "react";

import ProInput from "../form/Input";
import ProSelect from "../form/Select";

import {
  ENGINE_TYPES,
  TRANSMISSIONS,
  YEARS,
} from "../../data/tractors";

import { fetchBrands } from "../../api/brands";
import { fetchModelsByBrand } from "../../api/models";

export default function TractorCard({ index, remove }) {
  const { values, setFieldValue } = useFormikContext();
  const tractor = values.tractors[index];

  const [brandOptions, setBrandOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);

  const [brandsLoading, setBrandsLoading] = useState(false);
  const [modelsLoading, setModelsLoading] = useState(false);

  const [addingBrand, setAddingBrand] = useState(false);
  const [addingModel, setAddingModel] = useState(false);
  const [newBrand, setNewBrand] = useState("");
  const [newModel, setNewModel] = useState("");

  useEffect(() => {
    let active = true;

    const loadBrands = async () => {
      try {
        setBrandsLoading(true);
        const data = await fetchBrands();

        if (!active) return;

        setBrandOptions(
          (data || []).map((b) => ({
            label: b.name,
            value: b.name,
            id: b.id,
          }))
        );
      } catch (error) {
        if (!active) return;
        setBrandOptions([]);
      } finally {
        if (active) setBrandsLoading(false);
      }
    };

    loadBrands();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadModels = async () => {
      const brand = brandOptions.find((b) => b.value === tractor.brand);

      if (!brand?.id) {
        setModelOptions([]);
        return;
      }

      try {
        setModelsLoading(true);
        const data = await fetchModelsByBrand({ brandId: brand.id });

        if (!active) return;

        setModelOptions(
          (data || []).map((m) => ({
            label: m.name,
            value: m.name,
          }))
        );
      } catch (error) {
        if (!active) return;
        setModelOptions([]);
      } finally {
        if (active) setModelsLoading(false);
      }
    };

    loadModels();

    return () => {
      active = false;
    };
  }, [tractor.brand, brandOptions]);

  const updateModel = (brand, modelNumber) => {
    if (!brand || !modelNumber) return;
    setFieldValue(`tractors.${index}.model`, `${brand} ${modelNumber}`);
  };

  const finalBrandOptions = useMemo(
    () => [
      ...brandOptions,
      { label: "＋ Add new brand", value: "__add_brand__" },
    ],
    [brandOptions]
  );

  const finalModelOptions = useMemo(
    () => [
      ...modelOptions,
      { label: "＋ Add new model", value: "__add_model__" },
    ],
    [modelOptions]
  );

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-base dark:bg-slate-800">
            🚜
          </div>

          <div>
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">
              Tractor #{index + 1}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Tractor profile and specifications
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => remove(index)}
          className="rounded-lg px-3 py-1.5 text-sm font-medium text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
        >
          Remove
        </button>
      </div>

      {/* Body */}
      <div className="p-5">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {/* BRAND */}
          {!addingBrand ? (
            <div className="md:col-span-1">
              <ProSelect
                field={{
                  name: `tractors.${index}.brand`,
                  value: tractor.brand || "",
                }}
                form={{ errors: {}, touched: {} }}
                label="Brand"
                icon="🏷️"
                options={finalBrandOptions}
                onChange={(e) => {
                  const value = e.target.value;

                  if (value === "__add_brand__") {
                    setAddingBrand(true);
                    return;
                  }

                  setFieldValue(`tractors.${index}.brand`, value);
                  setFieldValue(`tractors.${index}.modelNumber`, "");
                  setFieldValue(`tractors.${index}.model`, "");
                  updateModel(value, "");
                }}
              />

              {brandsLoading && (
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  Loading brands...
                </p>
              )}
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 md:col-span-2 dark:border-slate-700 dark:bg-slate-950/40">
              <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                New Brand
              </label>

              <input
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500"
                placeholder="Enter new brand..."
                value={newBrand}
                onChange={(e) => setNewBrand(e.target.value)}
              />

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-xl bg-black px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90 dark:bg-white dark:text-black"
                  onClick={() => {
                    if (!newBrand.trim()) return;

                    setFieldValue(`tractors.${index}.brand`, newBrand.trim());
                    setFieldValue(`tractors.${index}.model`, "");
                    updateModel(newBrand.trim(), tractor.modelNumber);
                    setAddingBrand(false);
                    setNewBrand("");
                  }}
                >
                  Save Brand
                </button>

                <button
                  type="button"
                  className="rounded-xl px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
                  onClick={() => {
                    setAddingBrand(false);
                    setNewBrand("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* MODEL */}
          {!addingModel ? (
            <div className="md:col-span-1">
              <ProSelect
                field={{
                  name: `tractors.${index}.modelNumber`,
                  value: tractor.modelNumber || "",
                }}
                form={{ errors: {}, touched: {} }}
                label="Model Number"
                icon="🔢"
                options={finalModelOptions}
                onChange={(e) => {
                  const value = e.target.value;

                  if (value === "__add_model__") {
                    setAddingModel(true);
                    return;
                  }

                  setFieldValue(`tractors.${index}.modelNumber`, value);
                  updateModel(tractor.brand, value);
                }}
              />

              {modelsLoading && tractor.brand && (
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  Loading models...
                </p>
              )}
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 md:col-span-2 dark:border-slate-700 dark:bg-slate-950/40">
              <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                New Model Number
              </label>

              <input
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500"
                placeholder="Enter new model number..."
                value={newModel}
                onChange={(e) => setNewModel(e.target.value)}
              />

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-xl bg-black px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90 dark:bg-white dark:text-black"
                  onClick={() => {
                    if (!newModel.trim()) return;

                    setFieldValue(
                      `tractors.${index}.modelNumber`,
                      newModel.trim()
                    );
                    updateModel(tractor.brand, newModel.trim());
                    setAddingModel(false);
                    setNewModel("");
                  }}
                >
                  Save Model
                </button>

                <button
                  type="button"
                  className="rounded-xl px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
                  onClick={() => {
                    setAddingModel(false);
                    setNewModel("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* SERIAL */}
          <Field
            name={`tractors.${index}.serial`}
            component={ProInput}
            label="Serial Number"
            icon="🏷️"
          />

          {/* YEAR */}
          <Field
            name={`tractors.${index}.year`}
            component={ProSelect}
            label="Year of Manufacture"
            icon="📅"
            options={YEARS}
          />

          {/* ENGINE HOURS */}
          <Field
            name={`tractors.${index}.engineHours`}
            component={ProInput}
            label="Engine Hours"
            type="number"
            icon="⏱"
          />

          {/* ENGINE TYPE */}
          <Field
            name={`tractors.${index}.engineType`}
            component={ProSelect}
            label="Engine Type"
            icon="⚙️"
            options={ENGINE_TYPES}
          />

          {/* HORSE POWER */}
          <Field
            name={`tractors.${index}.horsePower`}
            component={ProInput}
            label="Horse Power"
            type="number"
            icon="💨"
          />

          {/* TRANSMISSION */}
          <Field
            name={`tractors.${index}.transmission`}
            component={ProSelect}
            label="Transmission"
            icon="🔧"
            options={TRANSMISSIONS}
          />
        </div>
      </div>
    </div>
  );
}